import { Fragment, useEffect, useRef, useState } from 'react';
import { apiFetch, isApiError } from '../../lib/api';
import {
  EVENT_VARIABLES,
  createWebhook,
  deleteWebhook,
  listDeliveries,
  listWebhooks,
  previewWebhook,
  resendDelivery,
  revealSigningSecret,
  rotateSigningSecret,
  testWebhook,
  updateWebhook,
  type DeliveryFilters,
  type DeliveryGroup,
  type DeliveryStatus,
  type PayloadFormat,
  type PreviewResult,
  type TestResult,
  type Webhook,
  type WebhookDraft,
  type WebhookEvent,
  type WebhookKind,
} from '../../lib/webhooks-api';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

const EVENT_LABEL: Record<WebhookEvent, string> = {
  'visitor.identified': 'Visitor identified (lead capture)',
  'session.started': 'Conversation started',
  'message.created': 'New message (visitor, agent or AI)',
  'session.assigned': 'Conversation assigned to an agent',
  'session.ended': 'Session ended (update lead)',
  'ticket.created': 'Ticket created',
  'ticket.updated': 'Ticket updated (status, priority, assignee)',
  'email.bounced': 'Support email bounced or dropped',
};

const PAYLOAD_LABEL: Record<PayloadFormat, string> = {
  standard: 'Standard JSON event',
  template: 'Custom template',
};

const KIND_LABEL: Record<WebhookKind, string> = {
  http: 'HTTP webhook',
  whatsapp: 'WhatsApp message',
};

// Plain-text WhatsApp message templates (no JSON). Sent to every site agent
// with a phone number.
const WHATSAPP_BODY: Record<WebhookEvent, string> = {
  'visitor.identified': `🟢 New visitor on {{site.name}}
Name: {{name}}
Email: {{email}}
Phone: {{phone}}
Page: {{context.page}}
{{context.city}}, {{context.country}}`,
  'session.ended': `💬 Chat ended on {{site.name}}
Visitor: {{name}} ({{email}})
Duration: {{duration}} · ended by {{ended_by}}
Summary: {{summary}}`,
  'ticket.created': `🎫 Ticket #{{ticket.number}} on {{site.name}}
{{ticket.subject}}
Priority: {{ticket.priority}} · Assignee: {{assignee.name}}
Customer: {{name}} ({{email}})`,
  'ticket.updated': `🎫 Ticket #{{ticket.number}} updated
{{ticket.subject}}
{{changes}}`,
  'session.started': `💬 New {{medium}} on {{site.name}} ({{channel}})
Visitor: {{name}} {{email}}
Page: {{context.page}}`,
  'message.created': `✉️ {{message.author_name}} on {{site.name}}:
{{message.text}}`,
  'session.assigned': `👤 {{agent.name}} took a conversation on {{site.name}}
Visitor: {{name}} ({{email}}) · from {{previous_owner}}`,
  'email.bounced': `⚠️ Email to {{recipient}} {{delivery_event}} on {{site.name}}
{{reason}}`,
};

function defaultBody(event: WebhookEvent, kind: WebhookKind): string {
  return kind === 'whatsapp' ? WHATSAPP_BODY[event] : DEFAULT_BODY[event];
}

const DEFAULT_BODY: Record<WebhookEvent, string> = {
  'visitor.identified': `{
  "name": "{{name}}",
  "email": "{{email}}",
  "phone": "{{phone}}",
  "site": "{{site.name}}",
  "context": {{context}}
}`,
  'session.ended': `{
  "name": "{{name}}",
  "email": "{{email}}",
  "phone": "{{phone}}",
  "transcript": "{{chat_transcript}}",
  "summary": "{{summary}}",
  "ended_by": "{{ended_by}}",
  "duration": "{{duration}}",
  "agent": "{{agent.name}}"
}`,
  'ticket.created': `{
  "number": {{ticket.number}},
  "subject": "{{ticket.subject}}",
  "priority": "{{ticket.priority}}",
  "status": "{{ticket.status}}",
  "assignee": "{{assignee.name}}",
  "customer": { "name": "{{name}}", "email": "{{email}}", "phone": "{{phone}}" },
  "site": "{{site.name}}"
}`,
  'ticket.updated': `{
  "number": {{ticket.number}},
  "subject": "{{ticket.subject}}",
  "status": "{{ticket.status}}",
  "priority": "{{ticket.priority}}",
  "assignee": "{{assignee.name}}",
  "changes": "{{changes}}"
}`,
  'session.started': `{
  "session_id": "{{session_id}}",
  "channel": "{{channel}}",
  "medium": "{{medium}}",
  "started_by": "{{started_by}}",
  "started_at": "{{started_at}}",
  "visitor": { "name": "{{name}}", "email": "{{email}}", "phone": "{{phone}}" },
  "page": "{{context.page}}"
}`,
  'message.created': `{
  "session_id": "{{session_id}}",
  "message_id": "{{message.id}}",
  "author": "{{message.author}}",
  "author_name": "{{message.author_name}}",
  "text": "{{message.text}}",
  "sent_at": "{{message.sent_at}}"
}`,
  'session.assigned': `{
  "session_id": "{{session_id}}",
  "agent": { "name": "{{agent.name}}", "email": "{{agent.email}}" },
  "previous_owner": "{{previous_owner}}",
  "via": "{{via}}",
  "assigned_at": "{{assigned_at}}"
}`,
  'email.bounced': `{
  "recipient": "{{recipient}}",
  "event": "{{delivery_event}}",
  "reason": "{{reason}}",
  "smtp_status": "{{smtp_status}}",
  "mail_kind": "{{mail_kind}}",
  "session_id": "{{session_id}}"
}`,
};

type EditDraft = WebhookDraft & { id?: string };

function blankDraft(event: WebhookEvent, kind: WebhookKind = 'http'): EditDraft {
  return {
    event,
    kind,
    name: '',
    url: '',
    method: 'POST',
    authType: 'none',
    authValue: '',
    headerName: '',
    headers: [],
    bodyTemplate: defaultBody(event, kind),
    // New HTTP hooks send the signed standard event; a template is one click away.
    payloadFormat: kind === 'http' ? 'standard' : 'template',
    enabled: true,
    requireVisitorInfo: false,
  };
}

// Map a saved hook into an editable draft. Migrates the legacy single-header
// auth ('header' + headerName/authValue) into a custom-headers row so the new
// multi-header editor shows it.
function toEditDraft(h: Webhook): EditDraft {
  const headers = [...(h.headers ?? [])];
  let authType = h.authType;
  let authValue = h.authValue ?? '';
  if (h.authType === 'header' && h.headerName) {
    headers.push({ name: h.headerName, value: h.authValue ?? '' });
    authType = 'none';
    authValue = '';
  }
  return { ...h, authType, authValue, headerName: '', headers };
}

export function WebhooksSection({ siteId }: { siteId: string }) {
  const [hooks, setHooks] = useState<Webhook[]>([]);
  const [signingAvailable, setSigningAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<EditDraft | null>(null);
  // The save gate: a hook is only persisted after a live test returns 2xx.
  const [testedOk, setTestedOk] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  async function load() {
    try {
      const r = await listWebhooks(siteId);
      setHooks(r.webhooks);
      setSigningAvailable(r.signingAvailable);
    } catch {
      setError('load-failed');
    }
  }
  useEffect(() => { void load(); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Changes to the request shape invalidate a prior passing test.
  function patchDraft(patch: Partial<EditDraft>) {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    if ('url' in patch || 'method' in patch || 'authType' in patch || 'authValue' in patch ||
        'headerName' in patch || 'headers' in patch || 'bodyTemplate' in patch || 'event' in patch ||
        'payloadFormat' in patch) {
      setTestedOk(false);
      setTestResult(null);
    }
  }

  function insertVar(v: string) {
    const token = `{{${v}}}`;
    const el = bodyRef.current;
    if (!el || !draft) return;
    const start = el.selectionStart ?? draft.bodyTemplate.length;
    const end = el.selectionEnd ?? start;
    const next = draft.bodyTemplate.slice(0, start) + token + draft.bodyTemplate.slice(end);
    patchDraft({ bodyTemplate: next });
    // Restore focus + caret after the inserted token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function runTest() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const r = await testWebhook(siteId, draft);
      setTestResult(r);
      setTestedOk(r.ok2xx);
    } catch {
      setError('test-failed');
      setTestedOk(false);
    } finally {
      setBusy(false);
    }
  }

  async function runPreview() {
    if (!draft) return;
    setBusy(true);
    try {
      setPreview(await previewWebhook(siteId, draft));
    } catch {
      setError('preview-failed');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!draft) return;
    // HTTP hooks must pass a 2xx test before saving; WhatsApp hooks have no
    // HTTP test, so they save directly.
    if (draft.kind === 'http' && !testedOk) return;
    setBusy(true);
    setError(null);
    try {
      const isWa = draft.kind === 'whatsapp';
      const payload: WebhookDraft = {
        ...draft,
        url: isWa ? '' : draft.url,
        authValue: isWa || draft.authType === 'none' ? null : draft.authValue || null,
        headerName: !isWa && draft.authType === 'header' ? draft.headerName || null : null,
        // Drop blank header rows (none for WhatsApp).
        headers: isWa ? [] : draft.headers.filter((h) => h.name.trim()),
        payloadFormat: isWa ? 'template' : draft.payloadFormat,
        lastTestStatus: isWa ? null : (testResult?.responseStatus ?? null),
      };
      if (draft.id) await updateWebhook(siteId, draft.id, payload);
      else await createWebhook(siteId, payload);
      setDraft(null);
      setTestResult(null);
      setPreview(null);
      await load();
    } catch (e) {
      setError(isApiError(e) ? `save-failed (${e.status})` : 'save-failed');
    } finally {
      setBusy(false);
    }
  }

  async function toggleEnabled(h: Webhook) {
    try {
      await updateWebhook(siteId, h.id, { enabled: !h.enabled });
      await load();
    } catch {
      setError('update-failed');
    }
  }

  async function remove(h: Webhook) {
    if (!(await confirmDialog({ title: `Delete webhook “${h.name}”?` }))) return;
    try {
      await deleteWebhook(siteId, h.id);
      await load();
    } catch {
      setError('delete-failed');
    }
  }

  return (
    <>
      <SessionEmailSettings siteId={siteId} />

      <section className={styles.section}>
        <p className={styles.kicker}>Webhooks</p>
        {error && <p className={styles.error} role="alert">{error}</p>}

        {hooks.length === 0 ? (
          <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>
            No webhooks yet. Send a signed HTTP request to your CRM, ticketing system or your own
            backend when a conversation starts, a message arrives, an agent takes over, a session
            ends, a ticket changes or a support email bounces.
          </p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Name</th><th>Event</th><th>URL</th><th>Payload</th><th>Enabled</th><th>Last test</th><th></th></tr>
              </thead>
              <tbody>
                {hooks.map((h) => (
                  <tr key={h.id}>
                    <td>{h.name}</td>
                    <td><span className={styles.badgeMuted}>{h.event}</span></td>
                    <td className={styles.mono} style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.kind === 'whatsapp' ? '📱 WhatsApp → site agents' : `${h.method} ${h.url}`}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {h.kind === 'whatsapp' ? '—' : (
                        <>
                          {h.payloadFormat === 'standard' ? 'Standard' : 'Template'}
                          {' · '}
                          <span className={h.signing ? styles.badgeAccent : styles.badgeMuted}>{h.signing ? 'signed' : 'unsigned'}</span>
                        </>
                      )}
                    </td>
                    <td>
                      <input type="checkbox" checked={h.enabled} onChange={() => void toggleEnabled(h)} aria-label={`Enable ${h.name}`} />
                    </td>
                    <td>{h.lastTestStatus != null ? h.lastTestStatus : '—'}</td>
                    <td style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
                      <button type="button" className={styles.buttonGhost} onClick={() => { setDraft(toEditDraft(h)); setTestedOk(false); setTestResult(null); setPreview(null); }}>
                        Edit
                      </button>
                      <button type="button" className={styles.buttonDanger} onClick={() => void remove(h)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!draft && (
          <div className={styles.actions} style={{ marginTop: 'var(--inic-space-3)' }}>
            <button type="button" className={styles.button} onClick={() => { setDraft(blankDraft('session.ended', 'http')); setTestedOk(false); setTestResult(null); setPreview(null); }}>
              Add webhook →
            </button>
            <button type="button" className={styles.buttonGhost} onClick={() => { setDraft(blankDraft('visitor.identified', 'whatsapp')); setTestedOk(false); setTestResult(null); setPreview(null); }}>
              Add WhatsApp message →
            </button>
          </div>
        )}

        {draft && (
          <div className={styles.form} style={{ marginTop: 'var(--inic-space-4)' }}>
            <label className={styles.field}>
              <span className={styles.label}>Event</span>
              <select className={styles.select} value={draft.event}
                onChange={(e) => patchDraft({ event: e.target.value as WebhookEvent, bodyTemplate: defaultBody(e.target.value as WebhookEvent, draft.kind) })}>
                {(Object.keys(EVENT_LABEL) as WebhookEvent[]).map((ev) => (
                  <option key={ev} value={ev}>{EVENT_LABEL[ev]}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Channel</span>
              <select className={styles.select} value={draft.kind}
                onChange={(e) => { const k = e.target.value as WebhookKind; patchDraft({ kind: k, bodyTemplate: defaultBody(draft.event, k) }); }}>
                {(Object.keys(KIND_LABEL) as WebhookKind[]).map((k) => (
                  <option key={k} value={k}>{KIND_LABEL[k]}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Name</span>
              <input className={styles.input} type="text" value={draft.name}
                onChange={(e) => patchDraft({ name: e.target.value })} placeholder="Push lead to HubSpot" />
            </label>
            {draft.kind === 'http' && (
              <label className={styles.field}>
                <span className={styles.label}>Payload</span>
                <select className={styles.select} value={draft.payloadFormat}
                  onChange={(e) => patchDraft({ payloadFormat: e.target.value as PayloadFormat })}>
                  {(Object.keys(PAYLOAD_LABEL) as PayloadFormat[]).map((f) => (
                    <option key={f} value={f}>{PAYLOAD_LABEL[f]}</option>
                  ))}
                </select>
              </label>
            )}
            {draft.kind === 'whatsapp' && (
              <p className={styles.field} style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
                Sends the message below over WhatsApp to <strong>every agent assigned to this site
                who has a phone number</strong>. Requires a linked WhatsApp number — set one up in{' '}
                <strong>Admin → WhatsApp</strong>. Add agent phone numbers under <strong>Admin → Agents</strong>.
              </p>
            )}
            {draft.kind === 'http' && (
            <label className={styles.field}>
              <span className={styles.label}>Method</span>
              <select className={styles.select} value={draft.method}
                onChange={(e) => patchDraft({ method: e.target.value as 'POST' | 'PUT' })}>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
              </select>
            </label>
            )}
            {draft.kind === 'http' && (
            <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <span className={styles.label}>URL</span>
              <input className={styles.input} type="text" value={draft.url}
                onChange={(e) => patchDraft({ url: e.target.value })}
                placeholder="https://api.yourcrm.com/leads" />
            </label>
            )}
            {draft.kind === 'http' && (
            <>
            <label className={styles.field}>
              <span className={styles.label}>Auth</span>
              <select className={styles.select} value={draft.authType === 'header' ? 'none' : draft.authType}
                onChange={(e) => patchDraft({ authType: e.target.value as WebhookDraft['authType'] })}>
                <option value="none">None</option>
                <option value="bearer">Bearer token</option>
                <option value="basic">Basic (user:pass)</option>
              </select>
            </label>
            {(draft.authType === 'bearer' || draft.authType === 'basic') && (
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>{draft.authType === 'basic' ? 'user:pass' : 'Token'}</span>
                <input className={styles.input} type="text" value={draft.authValue ?? ''}
                  onChange={(e) => patchDraft({ authValue: e.target.value })} placeholder="••••••••" />
              </label>
            )}

            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <span className={styles.label}>Custom headers — any number; values may use {`{{variables}}`}</span>
              {draft.headers.map((h, i) => {
                const isContentType = h.name.trim().toLowerCase() === 'content-type';
                return (
                <div key={i} style={{ display: 'flex', gap: 'var(--inic-space-2)', marginBottom: 'var(--inic-space-2)', alignItems: 'center' }}>
                  <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input className={styles.input} type="text" value={h.name} placeholder="Header name (e.g. X-API-Key)"
                      style={{ flex: 1 }}
                      onChange={(e) => patchDraft({ headers: draft.headers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                    {isContentType && (
                      <span
                        title={'Make the payload below match this type:\n• application/json → a JSON body  {"x": "{{name}}"}\n• application/x-www-form-urlencoded → form pairs  x={{name}}&y={{email}}\nValues are auto-escaped for whichever type you set here.'}
                        style={{ cursor: 'help', color: 'var(--inic-ink-muted)', border: '1px solid var(--inic-rule)', borderRadius: '50%', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}
                        aria-label="Payload must match the Content-Type"
                      >i</span>
                    )}
                  </span>
                  <input className={styles.input} type="text" value={h.value} placeholder="Value"
                    style={{ flex: 2 }}
                    onChange={(e) => patchDraft({ headers: draft.headers.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })} />
                  <button type="button" className={styles.buttonGhost}
                    onClick={() => patchDraft({ headers: draft.headers.filter((_, j) => j !== i) })}>Remove</button>
                </div>
                );
              })}
              <button type="button" className={styles.buttonGhost} style={{ alignSelf: 'flex-start' }}
                onClick={() => patchDraft({ headers: [...draft.headers, { name: '', value: '' }] })}>
                + Add header
              </button>
            </div>
            </>
            )}

            <label className={styles.field} style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 'var(--inic-space-2)' }}>
              <input
                type="checkbox"
                checked={draft.requireVisitorInfo}
                onChange={(e) => patchDraft({ requireVisitorInfo: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>
                <span className={styles.label} style={{ display: 'block' }}>Only fire when visitor info is available</span>
                <span style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
                  Skip this webhook unless the visitor has a name, email, or phone. Skipped fires are
                  recorded in the delivery log so you can see why nothing was sent.
                </span>
              </span>
            </label>

            {draft.kind === 'http' && draft.payloadFormat === 'standard' ? (
              <p style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
                Sends the event as JSON: <code>{'{ "id", "type", "created_at", "site", "data" }'}</code>, where{' '}
                <code>data</code> holds every variable of the event. <code>id</code> is the same on every retry
                (also sent as <code>X-Openline-Delivery</code>), so your endpoint can ignore repeats. Use
                “Debug (preview)” to see a sample.
              </p>
            ) : (
            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <span className={styles.label}>
                {draft.kind === 'whatsapp' ? 'Message' : 'Payload template'} — {`{{variables}}`} are replaced at fire time
              </span>
              {draft.kind === 'whatsapp' ? (
                <p style={{ margin: '0 0 var(--inic-space-2)', color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
                  Plain text — emoji and line breaks are fine. Each recipient gets this message with
                  the variables filled in. Empty variables (e.g. a missing phone) render as blank.
                </p>
              ) : (
                <p style={{ margin: '0 0 var(--inic-space-2)', color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
                  Tip: combine several variables and literal text in one value — no
                  operator needed, e.g. <code>{'"description": "Transcript: {{chat_transcript}} | Summary: {{summary}}"'}</code>.
                  String values are auto-escaped, so newlines or quotes in the text won’t break the JSON.
                </p>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--inic-space-1)', marginBottom: 'var(--inic-space-2)' }}>
                {EVENT_VARIABLES[draft.event].map((v) => (
                  <button key={v} type="button" className={styles.badgeAccent}
                    style={{ cursor: 'pointer', border: 'none' }} onClick={() => insertVar(v)}>
                    {v}
                  </button>
                ))}
              </div>
              <textarea ref={bodyRef} className={styles.input} rows={10} value={draft.bodyTemplate}
                onChange={(e) => patchDraft({ bodyTemplate: e.target.value })}
                style={{ resize: 'vertical', fontFamily: 'var(--inic-font-mono)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.45 }} />
            </div>
            )}

            {draft.kind === 'http' && draft.id && (
              <SigningSecretPanel siteId={siteId} hookId={draft.id} available={signingAvailable}
                signing={hooks.find((h) => h.id === draft.id)?.signing ?? false}
                onChanged={() => void load()} />
            )}
            {draft.kind === 'http' && !draft.id && (
              <p style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
                {signingAvailable
                  ? 'A signing secret is created with the webhook: every request carries X-Openline-Signature so your endpoint can check it came from Openline. Open the webhook again to copy the secret.'
                  : 'Requests will be unsigned: set CREDENTIALS_ENC_KEY on the server to sign webhooks.'}
              </p>
            )}

            {preview && (
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Resolved payload (sample data)</span>
                <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap' }}>{`${draft.method} ${preview.url}\n${Object.entries(preview.headers).map(([k, v]) => `${k}: ${v}`).join('\n')}\n\n${prettyBody(preview.body)}`}</pre>
              </div>
            )}
            {testResult && (
              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>
                  Test result —{' '}
                  <strong style={{ color: testResult.ok2xx ? 'var(--inic-ok, #15803d)' : 'var(--inic-danger, #b91c1c)' }}>
                    {testResult.responseStatus ?? 'no response'} {testResult.ok2xx ? '✓' : '✗'}
                  </strong>
                  {' '}· {testResult.durationMs} ms
                </span>
                <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>
                  {testResult.responseSnippet || '(empty response body)'}
                </pre>
              </div>
            )}

            <div className={styles.formActions}>
              {draft.kind === 'http' && (
                <>
                  <button type="button" className={styles.buttonGhost} onClick={() => void runPreview()} disabled={busy}>
                    Debug (preview)
                  </button>
                  <button type="button" className={styles.buttonGhost} onClick={() => void runTest()} disabled={busy || !draft.url}>
                    {busy ? 'Testing…' : 'Send test'}
                  </button>
                </>
              )}
              <button type="button" className={styles.button} onClick={() => void save()}
                disabled={busy || !draft.name || (draft.kind === 'http' && (!testedOk || !draft.url))}
                title={draft.kind === 'http' && !testedOk ? 'Send a test that returns 2xx before saving' : undefined}>
                {draft.id ? 'Save changes →' : draft.kind === 'whatsapp' ? 'Create WhatsApp hook →' : 'Create webhook →'}
              </button>
              <button type="button" className={styles.buttonGhost} onClick={() => { setDraft(null); setTestResult(null); setPreview(null); }} disabled={busy}>
                Cancel
              </button>
            </div>
            {draft.kind === 'http' && !testedOk && (
              <p style={{ gridColumn: '1 / -1', color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', margin: 0 }}>
                A webhook is saved only after a test fires successfully (2xx).
              </p>
            )}
          </div>
        )}
      </section>
    </>
  );
}

// ─── Per-site webhook delivery history (its own top-level "Webhook logs" tab) ───
export function WebhookLogsSection({ siteId }: { siteId: string }) {
  return <DeliveryHistory siteId={siteId} />;
}

// ─── Per-site session + chat-end email recipient settings ───
function SessionEmailSettings({ siteId }: { siteId: string }) {
  const [autoEndMinutes, setAutoEnd] = useState<number>(30);
  const [chatEndEmailTo, setEmailTo] = useState<string>('');
  const [requireVisitorInfo, setRequireVisitorInfo] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<{
    autoEndMinutes: number;
    chatEndEmailTo: string;
    requireVisitorInfoForEmail: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<{
      widgetConfig: {
        autoEndMinutes: number;
        chatEndEmailTo: string | null;
        requireVisitorInfoForEmail?: boolean;
      };
    }>(`/api/admin/sites/${siteId}/widget-config`).then((r) => {
      const m = r.widgetConfig.autoEndMinutes ?? 30;
      const e = r.widgetConfig.chatEndEmailTo ?? '';
      const req = r.widgetConfig.requireVisitorInfoForEmail ?? false;
      setAutoEnd(m);
      setEmailTo(e);
      setRequireVisitorInfo(req);
      setLoaded({ autoEndMinutes: m, chatEndEmailTo: e, requireVisitorInfoForEmail: req });
    }).catch(() => setErr('load-failed'));
  }, [siteId]);

  const dirty = loaded != null && (
    loaded.autoEndMinutes !== autoEndMinutes ||
    loaded.chatEndEmailTo !== chatEndEmailTo ||
    loaded.requireVisitorInfoForEmail !== requireVisitorInfo
  );

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await apiFetch(`/api/admin/sites/${siteId}/widget-config`, {
        method: 'PATCH',
        body: JSON.stringify({
          autoEndMinutes,
          chatEndEmailTo: chatEndEmailTo.trim() === '' ? null : chatEndEmailTo.trim(),
          requireVisitorInfoForEmail: requireVisitorInfo,
        }),
      });
      setLoaded({ autoEndMinutes, chatEndEmailTo, requireVisitorInfoForEmail: requireVisitorInfo });
    } catch (e) {
      setErr(isApiError(e) ? `save-failed (${e.status})` : 'save-failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Session &amp; chat-end email</p>
      {err && <p className={styles.error}>{err}</p>}
      <div className={styles.form}>
        <label className={styles.field}>
          <span className={styles.label}>Auto-end after inactivity (minutes)</span>
          <input className={styles.input} type="number" min={1} max={1440} value={autoEndMinutes}
            onChange={(e) => setAutoEnd(Math.max(1, Math.min(1440, Number(e.target.value) || 1)))} />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Chat-end email recipient</span>
          <input className={styles.input} type="email" value={chatEndEmailTo}
            onChange={(e) => setEmailTo(e.target.value)} placeholder="leads@yoursite.com" />
        </label>
        <label className={styles.field} style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 'var(--inic-space-2)' }}>
          <input
            type="checkbox"
            checked={requireVisitorInfo}
            onChange={(e) => setRequireVisitorInfo(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <span className={styles.label} style={{ display: 'block' }}>Only email when visitor info is available</span>
            <span style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
              Skip the chat-end email for anonymous chats (no name, email, or phone). Those chats are
              still recorded — they just don’t trigger a notification. An agent can still email a
              transcript manually from the conversation.
            </span>
          </span>
        </label>
        <div className={styles.formActions}>
          <button type="button" className={styles.button} onClick={() => void save()} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save →'}
          </button>
        </div>
        <p style={{ gridColumn: '1 / -1', color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', margin: 0 }}>
          The chat-end email is also sent to the agents who handled the session, and cc’d to the
          visitor when their email is known. Email sending is enabled in admin → System.
        </p>
      </div>
    </section>
  );
}

/** Pretty-print a JSON body; anything else as sent. */
function prettyBody(body: string | null): string {
  if (!body) return '';
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

const VERIFY_SNIPPET = `// Node.js — check X-Openline-Signature against the RAW request body.
const crypto = require('node:crypto');

function verifyOpenline(secret, header, rawBody) {
  const p = Object.fromEntries((header || '').split(',').map((kv) => kv.split('=')));
  if (!p.t || Math.abs(Date.now() / 1000 - Number(p.t)) > 300) return false; // replayed / stale
  const expected = crypto.createHmac('sha256', secret).update(p.t + '.' + rawBody).digest('hex');
  const got = Buffer.from(p.v1 || '', 'hex');
  return got.length === 32 && crypto.timingSafeEqual(got, Buffer.from(expected, 'hex'));
}`;

// ─── Signing secret (saved HTTP hooks) ───
function SigningSecretPanel({ siteId, hookId, signing, available, onChanged }: {
  siteId: string;
  hookId: string;
  signing: boolean;
  available: boolean;
  onChanged: () => void;
}) {
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function reveal() {
    setBusy(true); setMsg(null);
    try {
      setSecret(await revealSigningSecret(siteId, hookId));
    } catch {
      setMsg('Could not load the secret.');
    } finally {
      setBusy(false);
    }
  }

  async function rotate() {
    if (signing && !(await confirmDialog({ title: 'Rotate the signing secret?', message: 'The current one stops working immediately — update your endpoint with the new one.', confirmLabel: 'Rotate secret' }))) return;
    setBusy(true); setMsg(null);
    try {
      setSecret(await rotateSigningSecret(siteId, hookId));
      setMsg(signing ? 'New secret created. Requests are signed with it from now on.' : 'Signing turned on.');
      onChanged();
    } catch (e) {
      setMsg(isApiError(e) && e.status === 503 ? 'Set CREDENTIALS_ENC_KEY on the server to sign webhooks.' : 'Could not create a secret.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
      <span className={styles.label}>Signing secret</span>
      <p style={{ margin: '0 0 var(--inic-space-2)', color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.5 }}>
        {signing
          ? <>Every request carries <code>X-Openline-Signature: t=…,v1=…</code> — an HMAC-SHA256 of <code>t.body</code> with this secret. Check it on your endpoint and reject requests older than 5 minutes.</>
          : available
            ? 'This webhook sends unsigned requests. Turn signing on so your endpoint can check each request came from Openline.'
            : 'Unsigned: set CREDENTIALS_ENC_KEY on the server to sign webhooks.'}
      </p>
      {secret && (
        <div style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center', marginBottom: 'var(--inic-space-2)' }}>
          <code className={styles.mono} style={{ flex: 1, wordBreak: 'break-all' }}>{secret}</code>
          <button type="button" className={styles.buttonGhost}
            onClick={() => void navigator.clipboard?.writeText(secret).then(() => setMsg('Copied.'))}>Copy</button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 'var(--inic-space-2)', flexWrap: 'wrap' }}>
        {signing && !secret && (
          <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => void reveal()}>Reveal secret</button>
        )}
        {available && (
          <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => void rotate()}>
            {signing ? 'Rotate secret' : 'Turn on signing'}
          </button>
        )}
      </div>
      {msg && <p role="status" style={{ margin: 'var(--inic-space-2) 0 0', fontSize: 'var(--inic-size-xs)' }}>{msg}</p>}
      {signing && (
        <details style={{ marginTop: 'var(--inic-space-2)' }}>
          <summary className={styles.label} style={{ cursor: 'pointer' }}>How to verify a request</summary>
          <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap' }}>{VERIFY_SNIPPET}</pre>
        </details>
      )}
    </div>
  );
}

// ─── Delivery history ───

const STATUS_LABEL: Record<DeliveryStatus, string> = {
  delivered: 'Delivered',
  retrying: 'Retrying',
  failed: 'Failed',
  skipped: 'Skipped',
};

function statusBadge(status: DeliveryStatus) {
  const color = status === 'failed' ? 'var(--inic-danger, #b91c1c)' : undefined;
  return (
    <span className={status === 'delivered' ? styles.badgeAccent : styles.badgeMuted} style={color ? { color } : undefined}>
      {STATUS_LABEL[status]}
    </span>
  );
}

const time = (iso: string) => new Date(iso).toLocaleString();

function DeliveryHistory({ siteId }: { siteId: string }) {
  const [groups, setGroups] = useState<DeliveryGroup[]>([]);
  const [hooks, setHooks] = useState<Webhook[]>([]);
  const [filters, setFilters] = useState<DeliveryFilters>({});
  const [open, setOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Only the latest request may update the list: changing a filter while an
  // earlier (slower, unfiltered) load is in flight must not show its rows.
  const latest = useRef(0);

  async function load(f = filters) {
    const req = ++latest.current;
    setLoading(true);
    try {
      const rows = await listDeliveries(siteId, f);
      if (req !== latest.current) return;
      setGroups(rows);
      setError(null);
    } catch {
      if (req === latest.current) setError('Could not load the delivery history.');
    } finally {
      if (req === latest.current) setLoading(false);
    }
  }
  useEffect(() => {
    void listWebhooks(siteId).then((r) => setHooks(r.webhooks)).catch(() => {});
  }, [siteId]);
  useEffect(() => { void load(filters); }, [siteId, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  async function resend(g: DeliveryGroup) {
    if (!g.hookId || !g.eventId) return;
    setNotice(null); setError(null);
    try {
      await resendDelivery(siteId, g.hookId, g.eventId);
      setNotice(`Resending ${g.event} to ${g.hookName ?? 'the webhook'}. Refresh in a moment to see the attempt.`);
      setTimeout(() => void load(), 2500);
    } catch {
      setError('Could not resend: the webhook or the stored event no longer exists.');
    }
  }

  const setFilter = (patch: DeliveryFilters) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <section className={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p className={styles.kicker} style={{ margin: 0 }}>Delivery history</p>
        <button type="button" className={styles.buttonGhost} onClick={() => void load()}>Refresh</button>
      </div>
      <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)', margin: 'var(--inic-space-2) 0' }}>
        One row per event sent to a webhook. Failed sends are retried automatically up to 10 times over about
        half an hour; open a row to see every attempt, the request and the response. Kept for 30 days.
      </p>
      <div className={styles.form} style={{ marginBottom: 'var(--inic-space-3)' }}>
        <label className={styles.field}>
          <span className={styles.label}>Webhook</span>
          <select className={styles.select} aria-label="Filter by webhook" value={filters.hookId ?? ''}
            onChange={(e) => setFilter({ hookId: e.target.value || undefined })}>
            <option value="">All</option>
            {hooks.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Event</span>
          <select className={styles.select} aria-label="Filter by event" value={filters.event ?? ''}
            onChange={(e) => setFilter({ event: e.target.value || undefined })}>
            <option value="">All</option>
            {(Object.keys(EVENT_LABEL) as WebhookEvent[]).map((ev) => <option key={ev} value={ev}>{ev}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Status</span>
          <select className={styles.select} aria-label="Filter by status" value={filters.status ?? ''}
            onChange={(e) => setFilter({ status: (e.target.value || undefined) as DeliveryStatus | undefined })}>
            <option value="">All</option>
            {(Object.keys(STATUS_LABEL) as DeliveryStatus[]).map((st) => <option key={st} value={st}>{STATUS_LABEL[st]}</option>)}
          </select>
        </label>
      </div>
      {error && <p className={styles.error} role="alert">{error}</p>}
      {notice && <p className={styles.notice} role="status">{notice}</p>}
      {loading && groups.length === 0 ? (
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>Loading…</p>
      ) : groups.length === 0 ? (
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>No deliveries{Object.values(filters).some(Boolean) ? ' match these filters' : ' yet'}.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label="Webhook deliveries">
            <thead>
              <tr><th>When</th><th>Event</th><th>Webhook</th><th>Status</th><th>Attempts</th><th>Last response</th><th></th></tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const last = g.attempts[g.attempts.length - 1]!;
                const isOpen = open === g.key;
                return (
                  <Fragment key={g.key}>
                    <tr>
                      <td>{time(g.lastAt)}</td>
                      <td><span className={styles.badgeMuted}>{g.event}</span></td>
                      <td>{g.hookName ?? (g.kind === 'email' ? 'Chat-end email' : g.kind)}{g.kind === 'whatsapp' && g.target ? ` → ${g.target}` : ''}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {statusBadge(g.status)}
                        {g.nextRetryAt && <span style={{ display: 'block', fontSize: 'var(--inic-size-xs)', color: 'var(--inic-ink-muted)' }}>next try {new Date(g.nextRetryAt).toLocaleTimeString()}</span>}
                      </td>
                      <td>{g.attempts.length}</td>
                      <td className={styles.mono} style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={last.responseSnippet ?? ''}>
                        {last.responseStatus != null
                          ? `${last.responseStatus}${last.durationMs != null ? ` · ${last.durationMs} ms` : ''}`
                          : last.responseSnippet || '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 'var(--inic-space-2)', justifyContent: 'flex-end' }}>
                          <button type="button" className={styles.buttonGhost} aria-expanded={isOpen}
                            onClick={() => setOpen(isOpen ? null : g.key)}>{isOpen ? 'Hide' : 'Details'}</button>
                          {g.canResend && (
                            <button type="button" className={styles.buttonGhost} onClick={() => void resend(g)}>Resend</button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7}>
                          <DeliveryDetail group={g} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DeliveryDetail({ group }: { group: DeliveryGroup }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--inic-space-3)' }}>
      {group.eventId && (
        <p className={styles.mono} style={{ margin: 0, fontSize: 'var(--inic-size-xs)' }}>
          Event {group.eventId} · first sent {time(group.firstAt)}{group.target && group.kind === 'http' ? ` · ${group.target}` : ''}
        </p>
      )}
      {group.attempts.map((a, i) => (
        <div key={a.id} style={{ borderLeft: '2px solid var(--inic-rule)', paddingLeft: 'var(--inic-space-3)' }}>
          <p style={{ margin: 0, fontSize: 'var(--inic-size-sm)' }}>
            <strong>Attempt {a.attempt ?? i + 1}</strong>{a.manual ? ' (resend)' : ''} · {time(a.createdAt)} ·{' '}
            {a.status === 'success' ? '✓' : a.status === 'skipped' ? 'skipped' : '✗'}{' '}
            {a.responseStatus != null ? `HTTP ${a.responseStatus}` : ''}
            {a.durationMs != null ? ` · ${a.durationMs} ms` : ''}
            {a.nextRetryAt ? ` · retry scheduled ${new Date(a.nextRetryAt).toLocaleTimeString()}` : ''}
          </p>
          {a.responseSnippet && (
            <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto', margin: 'var(--inic-space-1) 0 0' }}>
              {a.responseSnippet}
            </pre>
          )}
          {(a.requestHeaders || a.requestBody) && (
            <details style={{ marginTop: 'var(--inic-space-1)' }}>
              <summary style={{ cursor: 'pointer', fontSize: 'var(--inic-size-xs)' }}>Request</summary>
              <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', maxHeight: 280, overflow: 'auto' }}>
                {`${Object.entries(a.requestHeaders ?? {}).map(([k, v]) => `${k}: ${v}`).join('\n')}\n\n${prettyBody(a.requestBody)}`}
              </pre>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}
